import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ReplayIcon from '@mui/icons-material/Replay';
import { CreateShiftProfileGroup } from 'components/dialog/shift_profile_group/create';
import { UpdateShiftProfileGroup } from 'components/dialog/shift_profile_group/update';
import {
    buildSectionSubpageActionPath,
    buildSectionSubpageBasePath,
    parseSectionSubpageActionRoute,
} from 'utils/sectionSubpageRouting';

interface SubMenuProps {
    selected: any;
    cleanSelected: () => void;
    rows: any;
    contextMenu: any;
    setContextMenu: (value: any) => void;
    reloading: () => void;
}

const SubMenu: FC<SubMenuProps> = ({ selected, cleanSelected, rows, contextMenu, setContextMenu, reloading }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    const [openAdd, setOpenAdd] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);

    const modalRoute = parseSectionSubpageActionRoute(location.pathname, 'schedule-rules', 'group-shift-profile', ['add', 'update']);
    const routeSelected = useMemo(() => {
        if (!modalRoute.id) return null;
        return rows.find((row: any) => String(row?.id) === String(modalRoute.id)) || null;
    }, [modalRoute.id, rows]);
    const selectedForDialog = routeSelected ? [routeSelected] : selected;

    const closeToBase = () => {
        const basePath = buildSectionSubpageBasePath('schedule-rules', 'group-shift-profile');
        if (modalRoute.action) {
            navigate(basePath);
            return;
        }

        setOpenAdd(false);
        setOpenEdit(false);
    };

    interface Option {
        name: string;
        icon: React.ReactNode;
        click: () => void;
        color: string;
        divider: boolean;
        disabled: boolean;
        selected: boolean;
        onlyOne: boolean;
        hidden: boolean;
    }

    const options: Option[] = [
        {
            name: 'Agregar',
            icon: <AddIcon />,
            click: () => {
                navigate(buildSectionSubpageActionPath('schedule-rules', 'group-shift-profile', 'add'));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: false,
            selected: false,
            onlyOne: false,
            hidden: false,
        },
        {
            name: 'Editar',
            icon: <EditIcon />,
            click: () => {
                if (!selected?.[0]?.id) return;
                navigate(buildSectionSubpageActionPath('schedule-rules', 'group-shift-profile', 'update', selected[0].id));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: selected.length === 1 ? false : true,
            selected: false,
            onlyOne: true,
            hidden: selected.length !== 1,
        },
        {
            name: t('accruals.accrual_codes.context_menu.reload'),
            icon: <ReplayIcon />,
            click: () => {
                reloading();
                setContextMenu(null);
            },
            color: '',
            divider: true,
            disabled: false,
            selected: false,
            onlyOne: false,
            hidden: false,
        }
    ];

    return (
        <>
            <Menu
                open={ Boolean(contextMenu) }
                onClose={() => setContextMenu(null) }
                anchorReference="anchorPosition"
                anchorPosition={ contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined }
            >
                {options.map((option) => (
                    <div key={option.name} hidden={option.onlyOne && selected?.length !== 1}>
                        {option.divider && <Divider />}
                        <MenuItem onClick={option.click} disabled={option.disabled} >
                            <ListItemIcon>{option.icon}</ListItemIcon>
                            <ListItemText primary={option.name} slotProps={{ primary: { color: option.color } }} />
                        </MenuItem>
                    </div>
                ))}
            </Menu>
            <CreateShiftProfileGroup open={modalRoute.action === 'add' || openAdd} setOpen={(value: boolean) => { if (!value) { closeToBase(); } else { setOpenAdd(true); } }} rows={rows} reloading={reloading} />
            <UpdateShiftProfileGroup open={modalRoute.action === 'update' || openEdit} setOpen={(value: boolean) => { if (!value) { closeToBase(); } else { setOpenEdit(true); } }} rows={rows} rowToEdit={selectedForDialog[0]} reloading={reloading} />
        </>
    );
};

export { SubMenu };
